import { useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { CalendarDays, LogOut, User, UserCircle } from 'lucide-react';
import { AnchoredDropdownMenu } from './AnchoredDropdownMenu';
import { Avatar } from './Avatar';
import { cn } from '@/lib/utils';
import {
	professionalHeaderActionIconClass,
	professionalHeaderProfileButtonClass,
} from './professionalHeaderConstants';
import { getProfessionalData } from '@/utils/cookies';
import { formatProfessionalFullName } from '@/utils/professionalName';
import { professionalHasB2CPermission } from '@/utils/professionalPermissionTags';
import { useLogoutProfessional } from '@/services/mutations/professional';

type ProfessionalLayoutContext = {
	data: Professionals.Professional | undefined;
	isLoading: boolean;
};

const getProfessionalInitials = (
	firstName?: string | null,
	lastName?: string | null,
) => {
	const first = firstName?.trim().charAt(0) ?? '';
	const last = lastName?.trim().charAt(0) ?? '';
	const initials = `${first}${last}`.toUpperCase();
	return initials || '?';
};

const ProfessionalHeaderProfileMenu = () => {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const { data: professionalFromContext } =
		useOutletContext<ProfessionalLayoutContext | null>() ?? {};
	const professional = professionalFromContext ?? getProfessionalData();
	const isAuthorized = Boolean(professional?.authorized);
	const canManageSchedules =
		isAuthorized && professionalHasB2CPermission(professional);
	const { mutate: logout, isPending: isLoggingOut } = useLogoutProfessional(
		() => navigate('/profissional/login'),
	);

	if (!professional?.id) {
		return null;
	}

	const avatarUrl = professional?.profile_photo_url ?? null;
	const fullName = professional
		? formatProfessionalFullName(professional)
		: 'Profissional';
	const email = professional?.email ?? '';
	const initials = getProfessionalInitials(
		professional?.first_name,
		professional?.last_name,
	);

	const closeMenu = () => setOpen(false);

	const handleProfile = () => {
		closeMenu();
		navigate('/profissional/perfil');
	};

	const handleSchedules = () => {
		closeMenu();
		navigate('/profissional/horarios');
	};

	const handleLogout = () => {
		closeMenu();
		logout();
	};

	return (
		<AnchoredDropdownMenu
			open={open}
			onOpenChange={setOpen}
			menuWidth={288}
			estimatedHeight={
				isAuthorized ? (canManageSchedules ? 288 : 240) : 72
			}
			menuClassName="w-72 max-w-[calc(100vw-1rem)] overflow-x-hidden p-0 animate-in fade-in zoom-in-95 duration-100"
			trigger={({
				ref: setAnchorRef,
				isOpen: menuOpen,
				onTriggerClick,
			}) => (
				<button
					ref={setAnchorRef}
					type="button"
					onClick={onTriggerClick}
					className={cn(
						professionalHeaderProfileButtonClass,
						'overflow-hidden p-0',
					)}
					aria-label="Abrir menu do perfil"
					aria-expanded={menuOpen}
					aria-haspopup="menu"
				>
					{avatarUrl ? (
						<Avatar
							className="size-full rounded-full"
							img_url={avatarUrl}
							variant="soft"
						/>
					) : (
						<div className="flex size-full items-center justify-center rounded-full bg-zinc-200 text-zinc-500">
							<UserCircle
								className={professionalHeaderActionIconClass}
							/>
						</div>
					)}
				</button>
			)}
		>
			<div className="flex flex-col">
				{isAuthorized ? (
					<>
						<div className="flex items-center gap-3 border-b border-stone-100 px-4 py-4">
							<div className="size-14 shrink-0 overflow-hidden rounded-full">
								{avatarUrl ? (
									<Avatar
										className="size-14 rounded-full"
										img_url={avatarUrl}
										variant="soft"
									/>
								) : (
									<div className="flex size-14 items-center justify-center rounded-full bg-blue-600 text-base font-bold text-white">
										{initials}
									</div>
								)}
							</div>

							<div className="min-w-0 flex-1 text-left">
								<p
									className="truncate text-base font-bold text-stone-900"
									title={fullName}
								>
									{fullName}
								</p>

								{email ? (
									<p
										className="mt-0.5 truncate text-xs text-stone-400"
										title={email}
									>
										{email}
									</p>
								) : null}
							</div>
						</div>

						<button
							type="button"
							onClick={handleProfile}
							className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50 focus:outline-none"
							role="menuitem"
						>
							<User
								size={18}
								className="shrink-0 text-stone-500"
								strokeWidth={2}
							/>
							Meu perfil
						</button>

						{canManageSchedules ? (
							<button
								type="button"
								onClick={handleSchedules}
								className="flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-semibold text-stone-700 transition-colors hover:bg-stone-50 focus:outline-none"
								role="menuitem"
							>
								<CalendarDays
									size={18}
									className="shrink-0 text-stone-500"
									strokeWidth={2}
								/>
								Meus horários
							</button>
						) : null}
					</>
				) : null}

				<div
					className={cn(isAuthorized && 'border-t border-stone-100')}
				>
					<button
						type="button"
						onClick={handleLogout}
						disabled={isLoggingOut}
						className={cn(
							'flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-bold text-red-600 transition-colors hover:bg-red-50 focus:outline-none',
							isLoggingOut && 'cursor-not-allowed opacity-60',
						)}
						role="menuitem"
					>
						<LogOut
							size={18}
							className="shrink-0"
							strokeWidth={2.25}
						/>
						Sair
					</button>
				</div>
			</div>
		</AnchoredDropdownMenu>
	);
};

export { ProfessionalHeaderProfileMenu };
